import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Eye, EyeOff, AlertCircle, Info, Loader2 } from 'lucide-react';
import { authService } from '../services/authService';
import type { Debtor, DemoCredential } from '../types';

interface LoginPageProps {
  onLogin: (debtor: Debtor) => void;
}

export default function LoginPage({ onLogin }: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [demoCredentials, setDemoCredentials] = useState<DemoCredential[]>([]);
  const [loadingCredentials, setLoadingCredentials] = useState(true);

  useEffect(() => {
    // Load demo credentials from dataset
    authService.getDemoCredentials()
      .then(setDemoCredentials)
      .finally(() => setLoadingCredentials(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await authService.login(email, password);
    
    setIsLoading(false);
    
    if (result.success && result.debtor) {
      onLogin(result.debtor);
    } else {
      setError(result.error || 'Login failed');
    }
  };

  const handleDemoLogin = (cred: DemoCredential) => {
    setEmail(cred.email);
    setPassword(cred.password);
  };

  const getSegmentColor = (segment: string) => {
    switch (segment) {
      case 'high': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <DollarSign className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">DFS Collections</h1>
          <p className="text-gray-600 mt-1">Debtor Self-Service Portal</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Sign In</h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Demo Credentials */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
              <Info className="w-4 h-4" />
              <span>Demo Accounts (click to fill)</span>
            </div>
            
            {loadingCredentials ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {demoCredentials.map((cred, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleDemoLogin(cred)}
                    className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 truncate">{cred.company}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getSegmentColor(cred.segment)}`}>
                        {cred.segment}
                      </span>
                    </div>
                    <span className="text-gray-500 text-xs">{cred.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Demo Info Link */}
          <div className="mt-4 text-center">
            <Link to="/demo-info" className="text-sm text-blue-600 hover:text-blue-700">
              Learn about this demo →
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-500">
          Powered by{' '}
          <a href="https://serenitystar.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
            Serenity★Star
          </a>
        </div>
      </div>
    </div>
  );
}
